import React, { memo, useState } from "react";
import { MathJax } from "better-react-mathjax";
import { Button } from "@material-tailwind/react";
import ModalBody from "../../../components/ui/ModalBody";
import useAuthStore from "../../../store/authStore";

const removeExtraClosingToken = (source, openToken, closeToken) => {
  let text = String(source || "");
  let result = "";
  let index = 0;
  let balance = 0;

  while (index < text.length) {
    if (text.startsWith(openToken, index)) {
      balance += 1;
      result += openToken;
      index += openToken.length;
      continue;
    }

    if (text.startsWith(closeToken, index)) {
      if (balance > 0) {
        balance -= 1;
        result += closeToken;
      }

      index += closeToken.length;
      continue;
    }

    result += text[index];
    index += 1;
  }

  if (balance > 0) {
    result += closeToken.repeat(balance);
  }

  return result;
};

const countUnescapedDollarSigns = (value) => {
  const text = String(value || "");
  let count = 0;

  for (let index = 0; index < text.length; index += 1) {
    if (text[index] !== "$") continue;

    let slashCount = 0;
    let cursor = index - 1;

    while (cursor >= 0 && text[cursor] === "\\") {
      slashCount += 1;
      cursor -= 1;
    }

    if (slashCount % 2 === 0) {
      count += 1;
    }
  }

  return count;
};

const isEscapedLatexAt = (text, index) => {
  let slashCount = 0;
  let cursor = index - 1;

  while (cursor >= 0 && text[cursor] === "\\") {
    slashCount += 1;
    cursor -= 1;
  }

  return slashCount % 2 === 1;
};

const balanceLatexCharacterPair = (value, openCharacter, closeCharacter) => {
  const text = String(value || "");
  let result = "";
  let balance = 0;

  for (let index = 0; index < text.length; index += 1) {
    const character = text[index];

    if (character === openCharacter && !isEscapedLatexAt(text, index)) {
      balance += 1;
      result += character;
      continue;
    }

    if (character === closeCharacter && !isEscapedLatexAt(text, index)) {
      if (balance > 0) {
        balance -= 1;
        result += character;
      }
      continue;
    }

    result += character;
  }

  return result + closeCharacter.repeat(balance);
};

const repairLatexMathBody = (value) => {
  let body = String(value || "")
    .replace(/\\(?:righ|rig|ri|r)(?=\s*$)/g, "")
    .replace(/\\left(?![A-Za-z])\s*/g, "")
    .replace(/\\right(?![A-Za-z])\s*/g, "");

  body = balanceLatexCharacterPair(body, "{", "}");
  body = balanceLatexCharacterPair(body, "[", "]");
  body = balanceLatexCharacterPair(body, "(", ")");

  return body.trim();
};

const repairLatexDelimitedBodies = (value) => {
  let text = String(value || "");

  text = text.replace(
    /\$\$([\s\S]*?)\$\$/g,
    (full, body) => `$$${repairLatexMathBody(body)}$$`,
  );

  text = text.replace(
    /\\\[([\s\S]*?)\\\]/g,
    (full, body) => `\\[${repairLatexMathBody(body)}\\]`,
  );

  text = text.replace(
    /\\\(([\s\S]*?)\\\)/g,
    (full, body) => `\\(${repairLatexMathBody(body)}\\)`,
  );

  text = text.replace(
    /\$([^$\n]*?)\$/g,
    (full, body) => `$${repairLatexMathBody(body)}$`,
  );

  return text;
};

const repairMalformedLatex = (value) => {
  let text = String(value || "");

  if (!text) return "";

  text = text
    .replace(/\\mathmr\b/g, "\\mathrm")
    .replace(/\\rm\s*\{/g, "\\mathrm{")
    .replace(
      /(?:Extra\s+\\left\s+or\s+missing\s+\\right|Missing\s+\\right|Extra\s+\\right)/gi,
      "",
    )
    .replace(/\\left(?![A-Za-z])\s*/g, "")
    .replace(/\\right(?![A-Za-z])\s*/g, "")
    .replace(/\\(?:righ|rig|ri|r|lef|le|l)(?=\s*(?:\$|\\\)|\\\]|<|$))/g, "");

  text = removeExtraClosingToken(text, "\\(", "\\)");
  text = removeExtraClosingToken(text, "\\[", "\\]");

  if (countUnescapedDollarSigns(text) % 2 !== 0) {
    text += "$";
  }

  text = repairLatexDelimitedBodies(text);

  return text
    .replace(/[ \t]+\n/g, "\n")
    .replace(/[ \t]{2,}/g, " ")
    .trim();
};
const PRINTED_CHOICE_LABEL_REGEX =
  /^\s*(?:\(\s*(?:[1-9]|[A-Ha-h]|VIII|VII|VI|IV|III|II|I|viii|vii|vi|iv|iii|ii|i)\s*\)|\[\s*(?:[1-9]|[A-Ha-h]|VIII|VII|VI|IV|III|II|I|viii|vii|vi|iv|iii|ii|i)\s*\]|(?:[1-9]|[A-Ha-h]|VIII|VII|VI|IV|III|II|I|viii|vii|vi|iv|iii|ii|i)\s*[\).,:;\-])\s*/i;

const collapseDuplicateOptionLabel = (value) => {
  const text = String(value || "").trim();
  const label =
    "(?:\\(\\s*(?:[1-9]|[A-Ha-h]|VIII|VII|VI|IV|III|II|I|viii|vii|vi|iv|iii|ii|i)\\s*\\)|" +
    "\\[\\s*(?:[1-9]|[A-Ha-h]|VIII|VII|VI|IV|III|II|I|viii|vii|vi|iv|iii|ii|i)\\s*\\]|" +
    "(?:[1-9]|[A-Ha-h]|VIII|VII|VI|IV|III|II|I|viii|vii|vi|iv|iii|ii|i)\\s*[\\).,:;\\-])";
  const match = text.match(
    new RegExp(`^\\s*(${label})\\s+(${label})\\s+`, "i"),
  );

  if (!match) return text;

  const first = String(match[1] || "")
    .replace(/\s+/g, "")
    .toLowerCase();
  const second = String(match[2] || "")
    .replace(/\s+/g, "")
    .toLowerCase();

  return first === second
    ? `${match[1]} ${text.slice(match[0].length)}`.trim()
    : text;
};

const QuestionNoStatus = memo(({ question, qSetId, onSave, solution }) => {
  const [editing, setEditing] = useState(null);
  const [editingType, setEditingType] = useState(null);
  const { client } = useAuthStore();

  function escapeHtmlAttr(value = "") {
    return String(value || "")
      .replace(/&/g, "&amp;")
      .replace(/"/g, "&quot;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;");
  }

  function hasVisibleContent(value) {
    return (
      String(value || "")
        .replace(/<[^>]*>/g, " ")
        .replace(/&nbsp;/gi, " ")
        .replace(/\s+/g, " ")
        .trim().length > 0
    );
  }

  function normalizeLatexText(html) {
    if (!html) return "";

    let text = repairMalformedLatex(html);
    const mathBlocks = [];

    const protectMath = (match) => {
      const key = `@@MATH_BLOCK_${mathBlocks.length}@@`;
      mathBlocks.push(repairMalformedLatex(match));
      return key;
    };

    text = text.replace(/\$\$[\s\S]*?\$\$/g, protectMath);
    text = text.replace(/\$[^$]*?\$/g, protectMath);
    text = text.replace(/\\\([\s\S]*?\\\)/g, protectMath);
    text = text.replace(/\\\[[\s\S]*?\\\]/g, protectMath);

    text = text
      .split("<br/>")
      .map((line) => {
        const clean = repairMalformedLatex(String(line || "").trim());

        if (!clean) return line;

        const hasRawLatex =
          /\\(frac|dfrac|tfrac|sqrt|Rightarrow|Leftarrow|times|cdot|text|mathrm|mu|alpha|beta|theta|sin|cos|tan|int|sum|ln|log)/.test(
            clean,
          ) || /[\^_]\{[^}]+\}/.test(clean);

        const hasHtmlTag = /<\/?[a-z][\s\S]*>/i.test(clean);

        if (hasRawLatex && !hasHtmlTag) {
          return `$${clean}$`;
        }

        return clean;
      })
      .join("<br/>");

    mathBlocks.forEach((block, index) => {
      text = text.replace(`@@MATH_BLOCK_${index}@@`, block);
    });

    return repairMalformedLatex(text);
  }

  function buildImageUrl(imageNameOrUrl) {
    const value = String(imageNameOrUrl || "").trim();

    if (!value) return "";

    if (/^https?:\/\//i.test(value)) {
      return value;
    }

    const s3Dir = client?.s3Dir || "";
    const currentQSetId = qSetId || question?.qSet || question?.qset || "";

    return `https://s3.us-west-004.backblazeb2.com/s3file/${s3Dir}/QSet/${currentQSetId}/${value}`;
  }

  function renderTextWithTags(text) {
    if (text === null || text === undefined) return "";

    let html = repairMalformedLatex(collapseDuplicateOptionLabel(text));
    const imagePlaceholders = [];
    const seenImages = new Set();

    const pushImage = (src, alt = "image") => {
      const finalSrc = String(src || "").trim();

      if (!finalSrc || seenImages.has(finalSrc)) {
        return "";
      }

      seenImages.add(finalSrc);
      const key = `@@AI_IMAGE_${imagePlaceholders.length}@@`;

      imagePlaceholders.push(
        `<br/><img src="${escapeHtmlAttr(finalSrc)}" alt="${escapeHtmlAttr(
          alt,
        )}" class="inline-block max-w-full h-auto my-2" />`,
      );

      return key;
    };

    html = html.replace(
      /!\[([^\]]*)\]\s*\(\s*(https?:\/\/[^)\s]+?\.(?:png|jpg|jpeg|webp|gif)(?:\?[^)\s]*)?)\s*\)/gi,
      (match, alt, url) => {
        return pushImage(url, alt || "image");
      },
    );

    html = html.replace(
      /!\[([^\]]*)\]\s*\(\s*((?:solution-)?image-[\w.-]+\.(?:png|jpg|jpeg|webp|gif))\s*\)/gi,
      (match, alt, imageName) => {
        return pushImage(buildImageUrl(imageName), alt || imageName);
      },
    );

    html = html.replace(
      /(^|[\s>])(https?:\/\/[^\s<)"']+\.(?:png|jpg|jpeg|webp|gif)(?:\?[^ \s<)"']*)?)/gi,
      (match, prefix, url) => {
        return `${prefix}${pushImage(url, "image")}`;
      },
    );

    html = html.replace(
      /(^|[\s>])((?:solution-)?image-[\w.-]+\.(?:png|jpg|jpeg|webp|gif))(?=($|[\s<]))/gi,
      (match, prefix, imageName) => {
        return `${prefix}${pushImage(buildImageUrl(imageName), imageName)}`;
      },
    );

    imagePlaceholders.forEach((imgHtml, index) => {
      html = html.replace(`@@AI_IMAGE_${index}@@`, imgHtml);
    });

    html = html.replace(/\n/g, "<br/>");
    return normalizeLatexText(html);
  }

  function RenderMathHtml({ html, className = "" }) {
    return (
      <MathJax dynamic>
        <div
          className={className}
          dangerouslySetInnerHTML={{
            __html: renderTextWithTags(html),
          }}
        />
      </MathJax>
    );
  }

  function cancelEdit() {
    setEditing(null);
    setEditingType(null);
  }

  const optionsEng = Array.isArray(question?.options_eng)
    ? question.options_eng
    : [];

  const optionsHin = Array.isArray(question?.options_hin)
    ? question.options_hin
    : [];

  const hasEnglishQuestion = hasVisibleContent(question?.question_eng);

  const hasHindiQuestion = hasVisibleContent(question?.question_hin);

  const answerKey = String(solution?.answer_key || "").trim();

  const englishSolution = String(solution?.solution_desc_eng || "").trim();

  const hindiSolution = String(solution?.solution_desc_hin || "").trim();

  const hasEnglishAnswerOrSolution =
    Boolean(answerKey) || hasVisibleContent(englishSolution);

  const hasHindiAnswerOrSolution =
    Boolean(answerKey) || hasVisibleContent(hindiSolution);

  return (
    <div>
      {editing && (
        <ModalBody
          question={{
            id: editing.id,
            qSetId,
            num: editing.question_num,
            ques:
              editingType === "eng"
                ? editing.question_eng
                : editing.question_hin,
            options:
              editingType === "eng"
                ? editing.options_eng || []
                : editing.options_hin || [],
            image: editing.image,
          }}
          open={Boolean(editing)}
          onSave={onSave}
          cancelEdit={cancelEdit}
          questionType={editingType}
        />
      )}

      <div className="flex justify-between p-3">
        {/* English Question Section */}
        <div className="w-1/2 text-wrap p-1 overflow-x-auto">
          {hasEnglishQuestion && (
            <>
              <div className="flex justify-between">
                <div>
                  <span className="font-semibold mr-2">
                    {question.question_num} .
                  </span>
                </div>

                <div className="opacity-0 group-hover:opacity-100">
                  <Button
                    color="red"
                    size="sm"
                    onClick={() => {
                      setEditing(question);
                      setEditingType("eng");
                    }}
                  >
                    Edit
                  </Button>
                </div>
              </div>

              <div className="p-2 text-lg font-semibold">
                <RenderMathHtml html={question.question_eng} />
              </div>

              {optionsEng.length > 0 && (
                <div>
                  <ol>
                    {optionsEng.map((option, index) => (
                      <li className="p-1" key={`eng-option-${index}`}>
                        <RenderMathHtml html={option} />
                      </li>
                    ))}
                  </ol>
                </div>
              )}

              {solution && hasEnglishAnswerOrSolution && (
                <div className="p-2 text-base">
                  {answerKey && (
                    <>
                      <span className="mr-2 font-medium">
                        Correct Option - {answerKey}
                      </span>
                      <br />
                    </>
                  )}

                  {hasVisibleContent(englishSolution) && (
                    <>
                      <p className="font-medium">Solution</p>

                      <RenderMathHtml html={englishSolution} />
                    </>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {/* Hindi Question Section */}
        <div className="w-1/2 text-wrap p-1 overflow-x-auto">
          {/*
           * Hindi question missing means the complete Hindi side stays
           * empty. Do not show question number, edit button, answer key,
           * English fallback solution, or an empty Solution heading.
           */}
          {hasHindiQuestion && (
            <>
              <div className="flex justify-between">
                <div>
                  <span className="font-semibold mr-2">
                    {question.question_num} .
                  </span>
                </div>

                <div className="opacity-0 group-hover:opacity-100">
                  <Button
                    color="red"
                    size="sm"
                    onClick={() => {
                      setEditing(question);
                      setEditingType("hin");
                    }}
                  >
                    Edit
                  </Button>
                </div>
              </div>

              <div className="p-2 text-lg font-semibold">
                <RenderMathHtml html={question.question_hin} />
              </div>

              {optionsHin.length > 0 && (
                <div>
                  <ol>
                    {optionsHin.map((option, index) => (
                      <li className="p-1" key={`hin-option-${index}`}>
                        <RenderMathHtml html={option} />
                      </li>
                    ))}
                  </ol>
                </div>
              )}

              {solution && hasHindiAnswerOrSolution && (
                <div className="p-2 text-base">
                  {answerKey && (
                    <>
                      <span className="mr-2 font-medium">
                        सही विकल्प - {answerKey}
                      </span>
                      <br />
                    </>
                  )}

                  {hasVisibleContent(hindiSolution) && (
                    <>
                      <p className="font-medium">हल</p>

                      {/*
                       * No English fallback here.
                       * Never use:
                       * solution_desc_hin || solution_desc_eng
                       */}
                      <RenderMathHtml html={hindiSolution} />
                    </>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
});

export default QuestionNoStatus;

// import React, { memo, useState } from "react";
// import { MathJax } from "better-react-mathjax";
// import { Button } from "@material-tailwind/react";
// import ModalBody from "../../../components/ui/ModalBody";
// import useAuthStore from "../../../store/authStore";

// const QuestionNoStatus = memo(({ question, qSetId, onSave, solution }) => {
//   const [editing, setEditing] = useState(null);
//   const [editingType, setEditingType] = useState(null);
//   const { client } = useAuthStore();

//   function escapeHtmlAttr(value = "") {
//     return String(value || "")
//       .replace(/&/g, "&amp;")
//       .replace(/"/g, "&quot;")
//       .replace(/</g, "&lt;")
//       .replace(/>/g, "&gt;");
//   }

//   function buildImageUrl(imageNameOrUrl) {
//     const value = String(imageNameOrUrl || "").trim();

//     if (!value) return "";

//     if (/^https?:\/\//i.test(value)) {
//       return value;
//     }

//     const s3Dir = client?.s3Dir || "435abcschool";
//     const currentQSetId = qSetId || question?.qSet || question?.qset || "";

//     return `https://s3.us-west-004.backblazeb2.com/s3file/${s3Dir}/QSet/${currentQSetId}/${value}`;
//   }

//   function protectBlocks(text) {
//     const blocks = [];

//     const protect = (match) => {
//       const key = `@@PROTECTED_BLOCK_${blocks.length}@@`;
//       blocks.push(match);
//       return key;
//     };

//     let output = String(text || "");

//     // Protect images first, then existing math blocks.
//     output = output.replace(/<img\b[^>]*>/gi, protect);
//     output = output.replace(/\$\$[\s\S]*?\$\$/g, protect);
//     output = output.replace(/\$[^$]*?\$/g, protect);
//     output = output.replace(/\\\([\s\S]*?\\\)/g, protect);
//     output = output.replace(/\\\[[\s\S]*?\\\]/g, protect);

//     return {
//       text: output,
//       restore(value) {
//         let restored = String(value || "");
//         blocks.forEach((block, index) => {
//           restored = restored.replace(`@@PROTECTED_BLOCK_${index}@@`, block);
//         });
//         return restored;
//       },
//     };
//   }

//   function getFirstRawLatexIndex(line) {
//     const patterns = [
//       /\\(?:frac|sqrt|Rightarrow|Leftarrow|rightarrow|leftarrow|times|cdot|text|mu|alpha|beta|theta|sin|cos|tan|int|sum|ln|log|overline|bar|leq|geq|neq|pi|pm|infty|arg|equiv|Delta|delta|left|right)\b/i,
//       /\\[a-zA-Z]+/i,
//       /[a-zA-Z0-9]\s*[\^_]\s*\{/,
//       /[\^_]\s*\{/,
//     ];

//     let minIndex = -1;

//     patterns.forEach((pattern) => {
//       const match = String(line || "").match(pattern);

//       if (match && match.index !== undefined) {
//         if (minIndex === -1 || match.index < minIndex) {
//           minIndex = match.index;
//         }
//       }
//     });

//     return minIndex;
//   }

//   function cleanLatexPart(value) {
//     return String(value || "")
//       .replace(/\$\$/g, "")
//       .replace(/\$/g, "")
//       .replace(/\\night/g, "\\right")
//       .replace(/\\left\s*\./g, "")
//       .replace(/\\right\s*\./g, "")
//       .trim();
//   }

//   function normalizeRawLatexSegment(segment) {
//     let line = String(segment || "");

//     if (!line.trim()) return line;

//     // Do not touch pure HTML segments except our protected placeholders.
//     if (/<(?!br\b|\/?span\b)[a-z][\s\S]*>/i.test(line)) {
//       return line;
//     }

//     const firstLatexIndex = getFirstRawLatexIndex(line);

//     if (firstLatexIndex === -1) {
//       return line;
//     }

//     const before = line.slice(0, firstLatexIndex);
//     const latexPart = cleanLatexPart(line.slice(firstLatexIndex));

//     if (!latexPart) {
//       return before;
//     }

//     /*
//       Important:
//       Earlier code wrapped the WHOLE line in $...$.
//       That made English/Hindi text look like weird italic math language.
//       Now only the raw LaTeX part is wrapped.
//     */
//     return `${before}\\(${latexPart}\\)`;
//   }

//   function normalizeLatexText(html) {
//     if (!html) return "";

//     let text = String(html);

//     const protectedData = protectBlocks(text);
//     text = protectedData.text;

//     // Preserve <br/> tokens while normalizing only text parts.
//     const parts = text.split(/(<br\s*\/?>)/gi);

//     text = parts
//       .map((part) => {
//         if (/^<br\s*\/?>$/i.test(part)) return part;
//         return normalizeRawLatexSegment(part);
//       })
//       .join("");

//     return protectedData.restore(text);
//   }

//   function renderTextWithTags(text) {
//     if (text === null || text === undefined) return "";

//     let html = String(text);
//     const imagePlaceholders = [];

//     const pushImage = (src, alt = "image") => {
//       const key = `@@AI_IMAGE_${imagePlaceholders.length}@@`;

//       imagePlaceholders.push(
//         `<br/><img src="${escapeHtmlAttr(src)}" alt="${escapeHtmlAttr(
//           alt,
//         )}" class="inline-block max-w-full h-auto my-2" />`,
//       );

//       return key;
//     };

//     /*
//       Supported image formats:
//       1. ![Solution Image](https://.../solution-image-xxx.png)
//       2. ![Solution Image]
//          (https://.../solution-image-xxx.png)
//       3. https://.../solution-image-xxx.png
//       4. image-xxx.png
//       5. solution-image-xxx.png
//     */

//     // Markdown image with full URL. Allows whitespace/newline between ] and (.
//     html = html.replace(
//       /!\[([^\]]*)\]\s*\(\s*(https?:\/\/[^)\s]+?\.(?:png|jpg|jpeg|webp|gif)(?:\?[^)\s]*)?)\s*\)/gi,
//       (match, alt, url) => {
//         return pushImage(url, alt || "image");
//       },
//     );

//     // Markdown image with only image filename. Allows whitespace/newline between ] and (.
//     html = html.replace(
//       /!\[([^\]]*)\]\s*\(\s*((?:solution-)?image-[\w.-]+\.(?:png|jpg|jpeg|webp|gif))\s*\)/gi,
//       (match, alt, imageName) => {
//         return pushImage(buildImageUrl(imageName), alt || imageName);
//       },
//     );

//     // Plain full image URL.
//     html = html.replace(
//       /(^|[\s>])(https?:\/\/[^\s<)"']+\.(?:png|jpg|jpeg|webp|gif)(?:\?[^ \s<)"']*)?)/gi,
//       (match, prefix, url) => {
//         return `${prefix}${pushImage(url, "image")}`;
//       },
//     );

//     // Plain image filename.
//     html = html.replace(
//       /(^|[\s>])((?:solution-)?image-[\w.-]+\.(?:png|jpg|jpeg|webp|gif))(?=($|[\s<]))/gi,
//       (match, prefix, imageName) => {
//         return `${prefix}${pushImage(buildImageUrl(imageName), imageName)}`;
//       },
//     );

//     imagePlaceholders.forEach((imgHtml, index) => {
//       html = html.replace(`@@AI_IMAGE_${index}@@`, imgHtml);
//     });

//     html = html.replace(/\n/g, "<br/>");
//     html = normalizeLatexText(html);

//     return html;
//   }

//   function RenderMathHtml({ html, className = "" }) {
//     return (
//       <MathJax dynamic>
//         <div
//           className={className}
//           dangerouslySetInnerHTML={{
//             __html: renderTextWithTags(html),
//           }}
//         />
//       </MathJax>
//     );
//   }

//   function cancelEdit() {
//     setEditing(null);
//     setEditingType(null);
//   }

//   const optionsEng = Array.isArray(question?.options_eng)
//     ? question.options_eng
//     : [];

//   const optionsHin = Array.isArray(question?.options_hin)
//     ? question.options_hin
//     : [];

//   return (
//     <div>
//       {editing && (
//         <ModalBody
//           question={{
//             id: editing.id,
//             qSetId,
//             num: editing.question_num,
//             ques:
//               editingType === "eng"
//                 ? editing.question_eng
//                 : editing.question_hin,
//             options:
//               editingType === "eng"
//                 ? editing.options_eng || []
//                 : editing.options_hin || [],
//             image: editing.image,
//           }}
//           open={!!editing}
//           onSave={onSave}
//           cancelEdit={cancelEdit}
//           questionType={editingType}
//         />
//       )}

//       <div className="flex justify-between p-3">
//         {/* English Question Section */}
//         <div className="w-1/2 text-wrap p-1 overflow-x-auto">
//           <div className="flex justify-between">
//             <div>
//               <span className="font-semibold mr-2">
//                 {question.question_num} .
//               </span>
//             </div>

//             <div className="opacity-0 group-hover:opacity-100">
//               <Button
//                 color="red"
//                 size="sm"
//                 onClick={() => {
//                   setEditing(question);
//                   setEditingType("eng");
//                 }}
//               >
//                 Edit
//               </Button>
//             </div>
//           </div>

//           <div className="p-2 text-lg font-semibold">
//             <RenderMathHtml html={question.question_eng} />
//           </div>

//           <div>
//             <ol>
//               {optionsEng.map((option, index) => (
//                 <li className="p-1" key={`eng-option-${index}`}>
//                   <RenderMathHtml html={option} />
//                 </li>
//               ))}
//             </ol>
//           </div>

//           {solution && (
//             <div className="p-2 text-base">
//               <span className="mr-2 font-medium">
//                 Correct Option - {solution.answer_key}
//               </span>
//               <br />
//               <p className="font-medium">Solution</p>

//               <RenderMathHtml html={solution.solution_desc_eng || ""} />
//             </div>
//           )}
//         </div>

//         {/* Hindi Question Section */}
//         <div className="w-1/2 text-wrap p-1 overflow-x-auto">
//           <div className="flex justify-between">
//             <div>
//               <span className="font-semibold mr-2">
//                 {question.question_num} .
//               </span>
//             </div>

//             <div className="opacity-0 group-hover:opacity-100">
//               <Button
//                 color="red"
//                 size="sm"
//                 onClick={() => {
//                   setEditing(question);
//                   setEditingType("hin");
//                 }}
//               >
//                 Edit
//               </Button>
//             </div>
//           </div>

//           <div className="p-2 text-lg font-semibold">
//             <RenderMathHtml html={question.question_hin} />
//           </div>

//           <div>
//             <ol>
//               {optionsHin.map((option, index) => (
//                 <li className="p-1" key={`hin-option-${index}`}>
//                   <RenderMathHtml html={option} />
//                 </li>
//               ))}
//             </ol>
//           </div>

//           {solution && (
//             <div className="p-2 text-base">
//               <span className="mr-2 font-medium">
//                 Correct Option - {solution.answer_key}
//               </span>
//               <br />
//               <p className="font-medium">Solution</p>

//               <RenderMathHtml
//                 html={
//                   solution.solution_desc_hin || solution.solution_desc_eng || ""
//                 }
//               />
//             </div>
//           )}
//         </div>
//       </div>
//     </div>
//   );
// });

// export default QuestionNoStatus;

// import React, { memo, useEffect, useState } from "react";
// import { MathJax } from "better-react-mathjax";
// import { Button } from "@material-tailwind/react";
// import ModalBody from "../../../components/ui/ModalBody";

// const QuestionNoStatus = memo(({ question, qSetId, onSave, solution }) => {
//   const [editing, setEditing] = useState(false);
//   const [editingType, setEditingType] = useState();
//  const { client } = useAuthStore();
//   function renderTextWithTags(text) {
//     if (text === null || text === undefined) return;
//      const s3Dir = client?.s3Dir || "";
//     console.log("s3Dir question", s3Dir);
//     return text.replace(/(image-[\w.-]+\.png)/g, (match, imageName) => {

//       return `<img src="https://s3.us-west-004.backblazeb2.com/s3file/435abcschool/QSet/${qSetId}/${imageName}" alt="${imageName}" class="inline max-w-full h-auto " />`;
//     });
//     //return text
//   }

//   function cancelEdit() {
//     setEditing(null);
//     setEditingType(null);
//   }

//   return (
//     <div>
//       {editing && (
//         <ModalBody
//           question={{
//             id: editing.id,
//             num: editing.question_num,
//             ques:
//               editingType === "eng"
//                 ? editing.question_eng
//                 : editing.question_hin,
//             options:
//               editingType === "hin" ? editing.options_eng : editing.options_hin,
//             image: editing.image,
//           }}
//           //open={editingIndex.index===i&&editingIndex.ques===0}
//           open={editing}
//           onSave={onSave}
//           cancelEdit={cancelEdit}
//           questionType={editingType}
//         />
//       )}

//       <div className={`flex justify-between p-3  `}>
//         {/* English Question Section*/}
//         <div className="w-1/2 text-wrap p-1 overflow-x-auto">
//           <div className="flex justify-between">
//             <div>
//               <span className="font-semibold mr-2">
//                 {question.question_num} .
//               </span>
//             </div>

//             <div className="opacity-0 group-hover:opacity-100">
//               <Button
//                 color="red"
//                 size="sm"
//                 onClick={() => {
//                   setEditing(question);
//                   setEditingType("eng");
//                 }}
//               >
//                 Edit
//               </Button>
//             </div>
//           </div>

//           <div className="p-2 text-lg font-semibold">
//             <p
//               dangerouslySetInnerHTML={{
//                 __html: renderTextWithTags(question.question_eng),
//               }}
//             ></p>
//           </div>

//           <div>
//             <ol>
//               {question["options_eng"].map((option, i) => (
//                 <li
//                   className="p-1 "
//                   key={i}
//                   dangerouslySetInnerHTML={{
//                     __html: renderTextWithTags(option),
//                   }}
//                 >
//                   {/* <span className="mr-2">({i + 1})</span> */}
//                   {/* {renderTextWithTags(option)}  */}
//                 </li>
//               ))}
//             </ol>
//           </div>

//           {solution && (
//             <div className="p-2 text-base ">
//               <span className="mr-2 font-medium">
//                 Correct Option - {solution.answer_key}
//               </span>{" "}
//               <br />
//               <p className="font-medium">Solution</p>
//               {renderTextWithTags(solution.solution_desc_eng)}
//             </div>
//           )}
//         </div>

//         {/* Hindi Question Section*/}
//         <div className="w-1/2 text-wrap p-1 overflow-x-auto">
//           <div className="flex justify-between">
//             <div>
//               <span className="font-semibold mr-2">
//                 {question.question_num} .
//               </span>
//             </div>

//             <div className="opacity-0 group-hover:opacity-100">
//               <Button
//                 color="red"
//                 size="sm"
//                 onClick={() => {
//                   setEditing(question);
//                   setEditingType("eng");
//                 }}
//               >
//                 Edit
//               </Button>
//             </div>
//           </div>

//           <div className="p-2 text-lg font-semibold">
//             <p
//               dangerouslySetInnerHTML={{
//                 __html: renderTextWithTags(question.question_hin),
//               }}
//             ></p>
//           </div>

//           <div>
//             <ol>
//               {question["options_hin"].map((option, i) => (
//                 <li
//                   className="p-1 "
//                   key={i}
//                   dangerouslySetInnerHTML={{
//                     __html: renderTextWithTags(option),
//                   }}
//                 >
//                   {/* <span className="mr-2">({i + 1})</span> */}
//                   {/* {renderTextWithTags(option)}  */}
//                 </li>
//               ))}
//             </ol>
//           </div>

//           {solution && (
//             <div className="p-2 text-base ">
//               <span className="mr-2 font-medium">
//                 Correct Option - {solution.answer_key}
//               </span>{" "}
//               <br />
//               <p className="font-medium">Solution</p>
//               {renderTextWithTags(solution.solution_desc_hin)}
//             </div>
//           )}
//         </div>
//       </div>
//     </div>
//   );
// });

// export default QuestionNoStatus;
